import React, { useState } from 'react';
import {
  ConfigProvider,
  Layout,
  Table,
  Tabs,
  Modal,
  Form,
  Input,
  Checkbox,
  Radio,
  Button,
  Breadcrumb,
  Avatar,
  Badge,
  Upload,
  Empty,
  message,
} from 'antd';
import {
  MenuOutlined,
  BarChartOutlined,
  PlusOutlined,
  FileTextOutlined,
  UserOutlined,
  TeamOutlined,
  PieChartOutlined,
  SettingOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  MessageOutlined,
  BellOutlined,
  DownOutlined,
  InboxOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import styled from 'styled-components';

const MINT = '#8FE3C8';
const MINT_DARK = '#74D4AF';
const SIDEBAR_BG = '#E4F5EE';
const TEXT_DARK = '#1F2937';
const TEXT_MUTED = '#6B7280';
const BORDER = '#ECECEC';
const BRAND_RED = '#C8102E';
const BRAND_NAVY = '#0B2A4A';

const themeConfig = {
  token: {
    colorPrimary: MINT,
    fontSize: 14,
    fontWeightStrong: 500,
    borderRadius: 6,
    colorText: TEXT_DARK,
  },
  components: {
    Button: {
      colorPrimary: MINT,
      colorPrimaryHover: MINT_DARK,
      colorPrimaryActive: MINT_DARK,
      primaryColor: TEXT_DARK,
      fontWeight: 500,
    },
    Table: {
      headerBg: '#FFFFFF',
      headerColor: TEXT_DARK,
      borderColor: BORDER,
      rowHoverBg: '#F3FCF8',
    },
    Tabs: {
      itemSelectedColor: TEXT_DARK,
      inkBarColor: MINT_DARK,
      itemHoverColor: TEXT_DARK,
    },
    Radio: {
      colorPrimary: MINT_DARK,
    },
    Checkbox: {
      colorPrimary: MINT_DARK,
    },
  },
};

const Shell = styled(Layout)`
  height: 100vh;
`;

const Sider = styled(Layout.Sider)`
  background: ${SIDEBAR_BG} !important;
  padding: 20px 0;

  .ant-layout-sider-children {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
`;

const SideItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 14px 0;
  cursor: pointer;
  font-size: 14px;
  font-weight: ${(p) => (p.$active ? 500 : 400)};
  color: ${TEXT_DARK};
  opacity: ${(p) => (p.$active ? 1 : 0.8)};

  .anticon {
    font-size: 20px;
  }
`;

const TopBar = styled(Layout.Header)`
  background: #ffffff !important;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 28px !important;
  height: 72px !important;
  border-bottom: 1px solid ${BORDER};
`;

const BrandWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const BrandMark = styled.div`
  width: 6px;
  align-self: stretch;
  background: ${BRAND_RED};
  border-radius: 2px;
`;

const BrandText = styled.div`
  display: flex;
  flex-direction: column;
  line-height: 1.1;
`;

const BrandTitle = styled.span`
  font-size: 22px;
  font-weight: 500;
  color: ${BRAND_NAVY};
`;

const BrandSub = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${BRAND_RED};
  letter-spacing: 0.5px;
`;

const TopBarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 22px;
`;

const AdminPill = styled(Button)`
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  height: 38px;
`;

const Page = styled(Layout.Content)`
  padding: 28px 32px;
  overflow-y: auto;
  background: #ffffff;
`;

const PageHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 22px;
`;

const PageTitle = styled.h1`
  font-size: 22px;
  font-weight: 500;
  color: ${TEXT_DARK};
  margin: 0;
`;

const SubText = styled.div`
  font-size: 14px;
  color: ${TEXT_MUTED};
  margin-bottom: 18px;
`;

const SearchBar = styled(Input)`
  width: 260px;
  border-radius: 8px;
  font-size: 14px;
`;

const MintButton = styled(Button)`
  background: ${MINT} !important;
  border-color: ${MINT} !important;
  color: ${TEXT_DARK} !important;
  font-weight: 500;
  border-radius: 8px;
  height: 40px;

  &:hover {
    background: ${MINT_DARK} !important;
    border-color: ${MINT_DARK} !important;
  }
`;

const StyledTable = styled(Table)`
  .ant-table-thead > tr > th {
    background: #ffffff;
    font-size: 14px;
    font-weight: 500;
    color: ${TEXT_DARK};
  }

  .ant-table-tbody > tr > td {
    font-size: 14px;
    color: ${TEXT_DARK};
    cursor: pointer;
  }

  .row-actions {
    opacity: 0;
    display: flex;
    gap: 14px;
    font-size: 16px;
    color: ${TEXT_MUTED};
    transition: opacity 0.15s ease;
  }

  .ant-table-row:hover .row-actions {
    opacity: 1;
  }

  .ant-table-row:hover > td {
    background: #F3FCF8 !important;
  }
`;

const EllipsisCell = styled.div`
  max-width: 640px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DetailCard = styled.div`
  border: 1px solid ${BORDER};
  border-radius: 10px;
  padding: 22px 26px;
  margin-bottom: 22px;
  position: relative;
`;

const DetailRow = styled.div`
  display: flex;
  gap: 60px;
  margin-bottom: 12px;
  font-size: 14px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const DetailLabel = styled.span`
  width: 130px;
  color: ${TEXT_MUTED};
  flex-shrink: 0;
`;

const DetailValue = styled.span`
  color: ${TEXT_DARK};
  font-weight: 500;
`;

const DetailActions = styled.div`
  position: absolute;
  top: 20px;
  right: 24px;
  display: flex;
  gap: 16px;
  font-size: 16px;
  color: ${TEXT_MUTED};
  cursor: pointer;
`;

const TabToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 16px 0;
`;

const FormWrap = styled.div`
  max-width: 900px;
`;

const FieldLabel = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${TEXT_DARK};
  margin-bottom: 8px;
`;

const FormFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 14px;
  margin-top: 28px;
`;

const GhostButton = styled(Button)`
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  height: 40px;
`;

const ModalList = styled.div`
  max-height: 320px;
  overflow-y: auto;
  margin-top: 6px;
`;

const ModalListRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px solid ${BORDER};
  font-size: 14px;

  &:last-child {
    border-bottom: none;
  }
`;

const ModalCode = styled.span`
  color: ${TEXT_MUTED};
`;

const ModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 20px;
`;

const BackLink = styled.a`
  font-size: 14px;
  font-weight: 500;
  color: ${TEXT_DARK};
`;

const FooterButtons = styled.div`
  display: flex;
  gap: 12px;
`;

const Dropzone = styled.div`
  border: 1px dashed #C9CED6;
  border-radius: 8px;
  padding: 36px 20px;
  text-align: center;
  background: #FAFAFB;

  .anticon {
    font-size: 26px;
    color: ${TEXT_MUTED};
    margin-bottom: 10px;
  }
`;

const DropzoneTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${TEXT_DARK};
  margin-bottom: 4px;
`;

const DropzoneSub = styled.div`
  font-size: 14px;
  color: ${TEXT_MUTED};

  a {
    color: #3B82F6;
    font-weight: 500;
  }
`;

const StagedFileRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 4px;
  font-size: 14px;
  color: ${TEXT_DARK};
`;

const DESIGN_DESCRIPTION =
  'A dedicated group for aspiring and professional designers alike. Dive into topics ranging from visual design principles to advanced prototyping, participate in hands-on projects, and learn from peer feedback. Enhance your portfolio while building connections with other designers.';

const initialGroups = [1, 2, 3, 4].map((n) => ({
  id: n,
  name: 'Design',
  key: '67ehjk',
  maxLimit: 20,
  description: DESIGN_DESCRIPTION,
  people: [],
  courses: [],
  files: [
    { name: 'Content', type: 'Pdf', size: '2 MB', uploadedOn: '20th Aug 2024; 08:10 PM' },
    { name: 'Course Material', type: 'img', size: '2 MB', uploadedOn: '20th Aug 2024; 08:10 PM' },
    { name: 'Content', type: 'Pdf', size: '2 MB', uploadedOn: '20th Aug 2024; 08:10 PM' },
    { name: 'Course Material', type: 'img', size: '2 MB', uploadedOn: '20th Aug 2024; 08:10 PM' },
  ],
}));

const employeeDirectory = [
  { name: 'Joseph Willaim', code: 'WA12345' },
  { name: 'Jeph', code: 'WA12345' },
  { name: 'Eva Thomson', code: 'WA12345' },
  { name: 'Joseph Willaim', code: 'WA12345' },
  { name: 'Joseph Willaim', code: 'WA12345' },
  { name: 'Eva Thomson', code: 'WA12345' },
  { name: 'Joseph Willaim', code: 'WA12345' },
  { name: 'Jeph', code: 'WA12345' },
  { name: 'Eva Thomson', code: 'WA12345' },
  { name: 'Joseph Willaim', code: 'WA12345' },
].map((e, i) => ({ ...e, id: i }));

const courseDirectory = [
  'General Tips to crack Interview',
  'Communication is the Key',
  'Design Thinking',
  'Finance Management',
  'General Tips to crack Interview',
  'Communication is the Key',
  'Design Thinking',
  'Finance Management',
  'General Tips to crack Interview',
  'Communication is the Key',
  'Design Thinking',
].map((name, i) => ({ id: i, name }));

const SideNav = ({ active }) => {
  const items = [
    { key: 'dashboard', label: 'Dashboard', icon: <BarChartOutlined /> },
    { key: 'course', label: 'Course', icon: <PlusOutlined /> },
    { key: 'courseList', label: 'Course List', icon: <FileTextOutlined /> },
    { key: 'people', label: 'People', icon: <UserOutlined /> },
    { key: 'group', label: 'Group', icon: <TeamOutlined /> },
    { key: 'reports', label: 'Reports', icon: <PieChartOutlined /> },
    { key: 'settings', label: 'Settings', icon: <SettingOutlined /> },
  ];

  return (
    <Sider width={110}>
      <SideItem $active={false}>
        <MenuOutlined />
      </SideItem>
      {items.map((item) => (
        <SideItem key={item.key} $active={item.key === active}>
          {item.icon}
          {item.label}
        </SideItem>
      ))}
    </Sider>
  );
};

const TopHeader = () => (
  <TopBar>
    <BrandWrap>
      <BrandMark />
      <BrandText>
        <BrandTitle>BDO</BrandTitle>
      </BrandText>
      <BrandText>
        <BrandSub>EAST AFRICA</BrandSub>
        <BrandSub>ACADEMY</BrandSub>
      </BrandText>
    </BrandWrap>
    <TopBarRight>
      <MessageOutlined style={{ fontSize: 18, color: TEXT_MUTED }} />
      <Badge dot color={BRAND_RED}>
        <BellOutlined style={{ fontSize: 18, color: TEXT_MUTED }} />
      </Badge>
      <Avatar size={34} icon={<UserOutlined />} />
      <AdminPill>
        Administrator <DownOutlined style={{ fontSize: 11 }} />
      </AdminPill>
    </TopBarRight>
  </TopBar>
);

const GroupListPage = ({ groups, onCreate, onOpen, onDelete }) => {
  const [search, setSearch] = useState('');

  const filtered = groups.filter((g) =>
    g.name.toLowerCase().includes(search.toLowerCase())
  );

  const columns = [
    { title: 'Name', dataIndex: 'name', width: 220 },
    {
      title: 'Description',
      dataIndex: 'description',
      render: (text) => <EllipsisCell>{text}</EllipsisCell>,
    },
    {
      title: '',
      dataIndex: 'actions',
      width: 90,
      render: (_, record) => (
        <div className="row-actions" onClick={(e) => e.stopPropagation()}>
          <EditOutlined onClick={() => onOpen(record.id)} />
          <DeleteOutlined onClick={() => onDelete(record.id)} />
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeaderRow>
        <PageTitle>Group</PageTitle>
        <MintButton icon={<PlusOutlined />} onClick={onCreate}>
          Create Group
        </MintButton>
      </PageHeaderRow>

      <PageHeaderRow>
        <SubText style={{ marginBottom: 0 }}>Total Group: {groups.length}</SubText>
        <SearchBar
          placeholder="Search"
          prefix={<SearchOutlined style={{ color: TEXT_MUTED }} />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </PageHeaderRow>

      <StyledTable
        rowKey="id"
        columns={columns}
        dataSource={filtered}
        pagination={false}
        onRow={(record) => ({ onClick: () => onOpen(record.id) })}
      />
    </>
  );
};

const CreateGroupPage = ({ onCancel, onCreated }) => {
  const [form] = Form.useForm();

  const handleFinish = (values) => {
    onCreated(values);
    form.resetFields();
  };

  return (
    <>
      <Breadcrumb
        items={[{ title: 'Group', onClick: onCancel, className: 'crumb-link' }, { title: 'Creating Group' }]}
        style={{ fontSize: 14, marginBottom: 20, cursor: 'pointer' }}
      />
      <FormWrap>
        <Form form={form} layout="vertical" onFinish={handleFinish}>
          <Form.Item
            label={<FieldLabel>Group Name</FieldLabel>}
            name="name"
            rules={[{ required: true, message: 'Please enter a group name' }]}
          >
            <Input size="large" placeholder="Enter group name" />
          </Form.Item>

          <Form.Item
            label={<FieldLabel>Description</FieldLabel>}
            name="description"
            rules={[{ required: true, message: 'Please enter a description' }]}
          >
            <Input.TextArea rows={4} placeholder="Enter description" />
          </Form.Item>

          <Form.Item name="hasKey" valuePropName="checked">
            <Checkbox>Group Key</Checkbox>
          </Form.Item>

          <FormFooter>
            <GhostButton onClick={onCancel}>Cancel</GhostButton>
            <MintButton htmlType="submit">Create</MintButton>
          </FormFooter>
        </Form>
      </FormWrap>
    </>
  );
};

const PeopleTab = ({ group, onAdd }) => {
  const columns = [
    { title: 'Name', dataIndex: 'name' },
    { title: 'Employee Code', dataIndex: 'code' },
  ];

  return (
    <>
      <TabToolbar>
        <SearchBar placeholder="Search" prefix={<SearchOutlined style={{ color: TEXT_MUTED }} />} />
        <MintButton icon={<PlusOutlined />} onClick={onAdd}>
          Add Employee
        </MintButton>
      </TabToolbar>
      <StyledTable
        rowKey="id"
        columns={columns}
        dataSource={group.people}
        pagination={false}
        locale={{ emptyText: <Empty description="No employees added yet" /> }}
      />
    </>
  );
};

const CourseTab = ({ group, onAdd }) => {
  const columns = [{ title: 'Course Name', dataIndex: 'name' }];

  return (
    <>
      <TabToolbar>
        <SearchBar placeholder="Search" prefix={<SearchOutlined style={{ color: TEXT_MUTED }} />} />
        <MintButton icon={<PlusOutlined />} onClick={onAdd}>
          Add Course
        </MintButton>
      </TabToolbar>
      <StyledTable
        rowKey="id"
        columns={columns}
        dataSource={group.courses}
        pagination={false}
        locale={{ emptyText: <Empty description="No courses added yet" /> }}
      />
    </>
  );
};

const FileTab = ({ group, onUpload }) => {
  const columns = [
    { title: 'File Name', dataIndex: 'name' },
    { title: 'Type', dataIndex: 'type' },
    { title: 'Size', dataIndex: 'size' },
    { title: 'Uploaded on', dataIndex: 'uploadedOn' },
  ];

  return (
    <>
      <TabToolbar>
        <SearchBar placeholder="Search" prefix={<SearchOutlined style={{ color: TEXT_MUTED }} />} />
        <MintButton icon={<PlusOutlined />} onClick={onUpload}>
          Upload File
        </MintButton>
      </TabToolbar>
      <StyledTable
        rowKey={(r, i) => i}
        columns={columns}
        dataSource={group.files}
        pagination={false}
        locale={{ emptyText: <Empty description="No files uploaded yet" /> }}
      />
    </>
  );
};

const GroupDetailPage = ({ group, onBack, onOpenAddEmployee, onOpenAddCourse, onOpenUpload }) => (
  <>
    <Breadcrumb
      items={[{ title: 'Group', onClick: onBack }, { title: group.name }]}
      style={{ fontSize: 14, marginBottom: 20, cursor: 'pointer' }}
    />

    <DetailCard>
      <DetailActions>
        <EditOutlined />
        <DeleteOutlined />
      </DetailActions>
      <DetailRow>
        <DetailLabel>Group Name</DetailLabel>
        <DetailValue>{group.name}</DetailValue>
      </DetailRow>
      <DetailRow>
        <DetailLabel>Group Key</DetailLabel>
        <DetailValue>{group.key}</DetailValue>
        <DetailLabel style={{ width: 110 }}>Maximum Limit</DetailLabel>
        <DetailValue>{group.maxLimit}</DetailValue>
      </DetailRow>
      <DetailRow>
        <DetailLabel>Description</DetailLabel>
        <DetailValue style={{ fontWeight: 400, color: TEXT_MUTED }}>{group.description}</DetailValue>
      </DetailRow>
    </DetailCard>

    <Tabs
      defaultActiveKey="people"
      items={[
        { key: 'people', label: 'People', children: <PeopleTab group={group} onAdd={onOpenAddEmployee} /> },
        { key: 'course', label: 'Course', children: <CourseTab group={group} onAdd={onOpenAddCourse} /> },
        { key: 'file', label: 'File', children: <FileTab group={group} onUpload={onOpenUpload} /> },
      ]}
    />
  </>
);

const AddEmployeeModal = ({ open, step, onNext, onBack, onCancel, onEnroll }) => {
  const [audience, setAudience] = useState('list');
  const [selected, setSelected] = useState([]);
  const allChecked = selected.length === employeeDirectory.length;

  const toggleAll = (e) => {
    setSelected(e.target.checked ? employeeDirectory.map((emp) => emp.id) : []);
  };

  const toggleOne = (id, checked) => {
    setSelected((prev) => (checked ? [...prev, id] : prev.filter((x) => x !== id)));
  };

  const handleEnroll = () => {
    const chosen = employeeDirectory.filter((emp) => selected.includes(emp.id));
    onEnroll(chosen);
    setSelected([]);
  };

  return (
    <Modal
      title={step === 'type' ? 'Add Employees to Group' : 'Choose employees to add'}
      open={open}
      onCancel={onCancel}
      footer={null}
      width={step === 'type' ? 460 : 480}
      closeIcon={<CloseOutlined />}
    >
      {step === 'type' && (
        <>
          <Radio.Group
            value={audience}
            onChange={(e) => setAudience(e.target.value)}
            style={{ display: 'flex', flexDirection: 'column', gap: 14, margin: '18px 0 28px' }}
          >
            <Radio value="list" style={{ fontSize: 14 }}>
              Show Employees list
            </Radio>
            <Radio value="category" style={{ fontSize: 14 }}>
              Employees by Category
            </Radio>
          </Radio.Group>
          <ModalFooter style={{ justifyContent: 'flex-end' }}>
            <FooterButtons>
              <GhostButton onClick={onCancel}>Cancel</GhostButton>
              <MintButton onClick={() => onNext(audience)}>Next</MintButton>
            </FooterButtons>
          </ModalFooter>
        </>
      )}

      {step === 'list' && (
        <>
          <Checkbox checked={allChecked} onChange={toggleAll} style={{ fontSize: 14, marginBottom: 4 }}>
            All
          </Checkbox>
          <ModalList>
            {employeeDirectory.map((emp) => (
              <ModalListRow key={emp.id}>
                <Checkbox
                  checked={selected.includes(emp.id)}
                  onChange={(e) => toggleOne(emp.id, e.target.checked)}
                >
                  {emp.name}
                </Checkbox>
                <ModalCode>{emp.code}</ModalCode>
              </ModalListRow>
            ))}
          </ModalList>
          <ModalFooter>
            <BackLink onClick={onBack}>Back</BackLink>
            <FooterButtons>
              <GhostButton onClick={onCancel}>Cancel</GhostButton>
              <MintButton onClick={handleEnroll}>Enroll</MintButton>
            </FooterButtons>
          </ModalFooter>
        </>
      )}
    </Modal>
  );
};

const AddCourseModal = ({ open, onBack, onCancel, onAdd }) => {
  const [selected, setSelected] = useState([]);
  const allChecked = selected.length === courseDirectory.length;

  const toggleAll = (e) => {
    setSelected(e.target.checked ? courseDirectory.map((c) => c.id) : []);
  };

  const toggleOne = (id, checked) => {
    setSelected((prev) => (checked ? [...prev, id] : prev.filter((x) => x !== id)));
  };

  const handleAdd = () => {
    const chosen = courseDirectory.filter((c) => selected.includes(c.id));
    onAdd(chosen);
    setSelected([]);
  };

  return (
    <Modal
      title="Choose course to add"
      open={open}
      onCancel={onCancel}
      footer={null}
      width={480}
      closeIcon={<CloseOutlined />}
    >
      <Checkbox checked={allChecked} onChange={toggleAll} style={{ fontSize: 14, marginBottom: 4 }}>
        All
      </Checkbox>
      <ModalList>
        {courseDirectory.map((c) => (
          <ModalListRow key={c.id}>
            <Checkbox checked={selected.includes(c.id)} onChange={(e) => toggleOne(c.id, e.target.checked)}>
              {c.name}
            </Checkbox>
          </ModalListRow>
        ))}
      </ModalList>
      <ModalFooter>
        <BackLink onClick={onBack}>Back</BackLink>
        <FooterButtons>
          <GhostButton onClick={onCancel}>Cancel</GhostButton>
          <MintButton onClick={handleAdd}>Add</MintButton>
        </FooterButtons>
      </ModalFooter>
    </Modal>
  );
};

const UploadFileModal = ({ open, onCancel, onSave }) => {
  const [staged, setStaged] = useState([]);

  const beforeUpload = (file) => {
    setStaged((prev) => [...prev, file]);
    return false;
  };

  const removeFile = (uid) => {
    setStaged((prev) => prev.filter((f) => f.uid !== uid));
  };

  const handleSave = () => {
    onSave(staged);
    setStaged([]);
  };

  return (
    <Modal
      title="Upload File"
      open={open}
      onCancel={onCancel}
      footer={null}
      width={480}
      closeIcon={<CloseOutlined />}
    >
      <Upload.Dragger multiple showUploadList={false} beforeUpload={beforeUpload}>
        <Dropzone>
          <InboxOutlined />
          <DropzoneTitle>Drag files here to upload</DropzoneTitle>
          <DropzoneSub>
            Alternatively, you can select file by <a>Clicking here</a>
          </DropzoneSub>
        </Dropzone>
      </Upload.Dragger>

      {staged.length > 0 && (
        <div style={{ marginTop: 14 }}>
          {staged.map((f) => (
            <StagedFileRow key={f.uid}>
              {f.name}
              <CloseOutlined onClick={() => removeFile(f.uid)} style={{ cursor: 'pointer', color: TEXT_MUTED }} />
            </StagedFileRow>
          ))}
        </div>
      )}

      <FormFooter>
        <GhostButton onClick={onCancel}>Cancel</GhostButton>
        <MintButton onClick={handleSave}>Save</MintButton>
      </FormFooter>
    </Modal>
  );
};

export default function GroupManagement() {
  const [groups, setGroups] = useState(initialGroups);
  const [view, setView] = useState('list');
  const [selectedId, setSelectedId] = useState(null);

  const [empModalOpen, setEmpModalOpen] = useState(false);
  const [empStep, setEmpStep] = useState('type');
  const [courseModalOpen, setCourseModalOpen] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);

  const selectedGroup = groups.find((g) => g.id === selectedId);

  const goToList = () => {
    setView('list');
    setSelectedId(null);
  };

  const goToCreate = () => setView('create');

  const openGroup = (id) => {
    setSelectedId(id);
    setView('detail');
  };

  const deleteGroup = (id) => {
    setGroups((prev) => prev.filter((g) => g.id !== id));
    message.success('Group deleted');
  };

  const createGroup = (values) => {
    const newGroup = {
      id: Date.now(),
      name: values.name,
      key: values.hasKey ? Math.random().toString(36).slice(2, 8) : '-',
      maxLimit: 20,
      description: values.description,
      people: [],
      courses: [],
      files: [],
    };
    setGroups((prev) => [...prev, newGroup]);
    message.success('Group created');
    goToList();
  };

  const updateGroup = (id, updater) => {
    setGroups((prev) => prev.map((g) => (g.id === id ? updater(g) : g)));
  };

  const openAddEmployee = () => {
    setEmpStep('type');
    setEmpModalOpen(true);
  };

  const handleEnroll = (chosen) => {
    updateGroup(selectedId, (g) => ({ ...g, people: [...g.people, ...chosen] }));
    setEmpModalOpen(false);
    message.success('Enrolled');
  };

  const handleAddCourses = (chosen) => {
    updateGroup(selectedId, (g) => ({ ...g, courses: [...g.courses, ...chosen] }));
    setCourseModalOpen(false);
    message.success('Added');
  };

  const handleSaveFiles = (staged) => {
    const now = new Date();
    const mapped = staged.map((f) => ({
      name: f.name.replace(/\.[^/.]+$/, ''),
      type: f.name.split('.').pop().toUpperCase(),
      size: '2 MB',
      uploadedOn: now.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) +
        '; ' +
        now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
    }));
    updateGroup(selectedId, (g) => ({ ...g, files: [...g.files, ...mapped] }));
    setUploadModalOpen(false);
    message.success('Saved');
  };

  return (
    <ConfigProvider theme={themeConfig}>
      <Shell>
        <SideNav active="group" />
        <Layout>
          <TopHeader />
          <Page>
            {view === 'list' && (
              <GroupListPage groups={groups} onCreate={goToCreate} onOpen={openGroup} onDelete={deleteGroup} />
            )}
            {view === 'create' && <CreateGroupPage onCancel={goToList} onCreated={createGroup} />}
            {view === 'detail' && selectedGroup && (
              <GroupDetailPage
                group={selectedGroup}
                onBack={goToList}
                onOpenAddEmployee={openAddEmployee}
                onOpenAddCourse={() => setCourseModalOpen(true)}
                onOpenUpload={() => setUploadModalOpen(true)}
              />
            )}
          </Page>
        </Layout>
      </Shell>

      <AddEmployeeModal
        open={empModalOpen}
        step={empStep}
        onNext={() => setEmpStep('list')}
        onBack={() => setEmpStep('type')}
        onCancel={() => setEmpModalOpen(false)}
        onEnroll={handleEnroll}
      />

      <AddCourseModal
        open={courseModalOpen}
        onBack={() => setCourseModalOpen(false)}
        onCancel={() => setCourseModalOpen(false)}
        onAdd={handleAddCourses}
      />

      <UploadFileModal
        open={uploadModalOpen}
        onCancel={() => setUploadModalOpen(false)}
        onSave={handleSaveFiles}
      />
    </ConfigProvider>
  );
}